import React, { useState } from "react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, UserPen } from "lucide-react";
import { cn } from "@/lib/utils";
import StatusBadge from "@component/statusbadgestailwind/StatusBadge";
import EllipsisTooltip from "@component/tooltip/EllipsisTooltip";

export function ExpandableOverviewCard({
  id = "",
  statusType = "custom",
  rightComponent,
  data,
  onButtonClick,
  user = false,
  component: Component,
  variant = "",
  isLoading = false,
  showBorderVariant = true,
  isDefaultExpanded = false,
  icon,
  isNeedExpand = true,
  label = '',
  title,
  statusbar = false,
  status,
  children,
  hubspot_url = '', // Controls whether to show HubSpot icon in the collapsible trigger
  showStatus = true,
  demandTypeId = ""
}) {  
  const [isExpanded, setIsExpanded] = useState(isDefaultExpanded);

  const ProjectTitle = title ? title : data?.title || "";
  const Status = status ? status : data?.status || "";
  const CompanyName = data?.companyname || "";
  const isCollaborator = data?.collaborator || "";
  const modelPrefixes = ["DR", "LEGR", "LEG", "SCOPED", "RM","DEMAND","Scoped","Qualified","Discovery"];
  const model = modelPrefixes.find(prefix => Status.startsWith(prefix));

  // Only include badges if statusType is not "none" and Status is not empty
  let statusBadges = []
  if (model) {
    statusBadges.push({ variant: "", statusType: "model", status: model, demandTypeId: demandTypeId });
  }

  if (statusType !== "none" && Status) {
    statusBadges.push({ variant: variant || "", statusType: statusType || "custom", status: Status });
  }

  if (Array.isArray(data?.extraStatusBadges)) {
    statusBadges = statusBadges.concat(data.extraStatusBadges);
  }

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className={`w-full rounded-lg bg-primary-foreground border border-card-border shadow-sm p-4 flex flex-col gap-4`}
    >
      {/* Top Toolbar */}
      <div
        className="flex flex-wrap justify-between items-start gap-4 cursor-pointer"
        onClick={() => isNeedExpand && setIsExpanded(!isExpanded)}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {isLoading ? (
              <Skeleton className="h-6 w-48 dark:bg-gray-600" />
            ) : (
              <>
                {icon && <img src={icon} alt="icon" className="w-8 h-8" />}
              <h2
                className={`text-xl font-bold text-foreground ${user ? "leading-7" : "leading-6"
                  }`}
              >
                {ProjectTitle}
              </h2>
              </>
            )}
            {isLoading ? (
              <Skeleton className="h-6 w-10 rounded-md dark:bg-gray-600" />
            ) : (
              showStatus && statusBadges.length > 0 &&
              statusBadges.map((badge, idx) => (
                <StatusBadge
                  key={`${badge.statusType}-${idx}`}
                  variant={badge.variant}
                  statusType={badge.statusType}
                  status={badge.status}
                  demandTypeId={badge.demandTypeId}
                />
              ))
            )}
          </div>

          {isLoading ? (
            <Skeleton className="h-4 w-36 dark:bg-gray-600" />
          ) : (
            <p className="text-sm text-muted-foreground">
              {CompanyName || ""}
            </p>
          )}
        </div>
        {isCollaborator && 
          <EllipsisTooltip count={0} title="Collaborator">
            <div className="bg-primary/20 p-1.5 rounded-full">
              <UserPen className="w-4 h-4" />
            </div>
          </EllipsisTooltip>
        }

        <div className="flex items-center justify-center gap-3 flex-wrap self-center"
          onClick={(e) => e.stopPropagation()} // stop toggle when clicking buttons
        >
          {rightComponent && (
            <div className="ml-2 w-fit min-w-max">{rightComponent}</div>
          )}
          {isNeedExpand && (
            <CollapsibleTrigger asChild>
              <div className=" flex items-center justify-center pr-4 gap-2">
                {/* Optional HubSpot URL - shows when hubspot_url prop is true */}
                {hubspot_url && (
                  <a 
                    href={hubspot_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()} // Prevent card expansion when clicking link
                  >
                    View deal in Hubspot
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}

                {/* Chevron with rotation */}
                <ChevronDown
                  className={cn(
                    "!h-6 !w-6 transform transition-transform duration-300 text-muted-foreground",
                    isExpanded ? "rotate-180" : "rotate-0"
                  )}
                />
              </div>
            </CollapsibleTrigger>
          )}

        </div>

      </div>


      <CollapsibleContent className="space-y-4">
        {Component && <Component />}

        <div
          className={cn(
            "flex flex-row  flex-wrap",
            statusbar ? "justify-between gap-x-0 w-[95%]" : "w-full justify-start gap-x-20 gap-y-4"
          )}
        >
          {(!Array.isArray(data?.[label]) || data[label].length === 0)
            ? Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex flex-col gap-1">
                <Skeleton className="h-4 w-24 dark:bg-gray-600" />
                <Skeleton className="h-5 w-20 dark:bg-gray-600" />
              </div>
            ))
            : data[label]?.map((item, index) => (
              <React.Fragment key={index}>

                <div className="flex items-center gap-5">
                  <div className="flex flex-col gap-0">
                    <p className="text-[0.75rem] text-muted-foreground font-sans">
                      {item?.title}
                    </p>
                    <div className="flex flex-row gap-1 items-center">
                      {item?.icon && (
                        <img
                          src={item?.icon}
                          alt="icon"
                          className="w-5 h-5"
                        />
                      )}
                      {isLoading ? (
                        <Skeleton className="h-5 w-20 dark:bg-gray-600" />
                      ) : (
                        <p className="font-semibold text-[1rem] text-foreground font-sans capitalize first-letter:uppercase">
                          {typeof item?.subtitle === "function"
                            ? item?.subtitle()
                            : item?.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))}
          {/* {statusbar && <div className="w-[-10%]" />} */}
        </div>
        {children && <div className="pt-2">{children}</div>}
      </CollapsibleContent>

    </Collapsible>
  );
}
